import { useEffect, useRef, useState } from 'react';
import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { addError } from '@mfe-helpers/datadog';
import { debouncePromise } from '@mfe-helpers/debouncePromise';
import {
    fetchAutocompleteAPI,
    fetchGeocodingAPI,
    focusOnNextField,
    getPortalCountry,
    getPostalCodeLength,
} from '@mfe-helpers/location/common';
import { computeCitiesValues } from '@mfe-helpers/location/extractor';
import {
    fetchAutocompleteApiForHandover,
    fetchMappingApiFromLegacyData,
    fetchPlaceApi,
    generateHandoverAutocompletePayload,
    getLegacyLocationParamsFromURL,
    getLocationParamsFromURL,
    getParamFromURL,
} from '@mfe-helpers/location/handover';
import FieldsetWrapper from '@mfe-presentation/components/Wrappers/FieldsetWrapper';
import FieldWrapper from '@mfe-presentation/components/Wrappers/FieldWrapper';
import { useAppContext, useAppDispatch } from '@mfe-presentation/contexts';
import type { TStyleProps } from '@mfe-types/box';
import { EActionType } from '@mfe-types/context';
import type { Field } from '@mfe-types/field';
import type {
    AutocompleteRequestPayload,
    City,
    LegacyPlaceParams,
    PlaceParams,
    PlaceParamsWithAtLeastPostalCode,
} from '@mfe-types/geo';
import { LocationError } from '@mfe-types/geo';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocale, useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { Box } from '@gemini/core';
import { Global, CSSObject } from '@emotion/react';
import { isFrenchPortal } from '@mfe-helpers/portalHelpers';

const addressContainerStyles: TStyleProps = {
    display: 'flex',
    flexDirection: 'row',
};

const streetNameStyles: TStyleProps = {
    maxWidth: 'calc(100% - 72px - 8px)', // used for visual stability when clear icon appears
};

const houseNumberStyles: TStyleProps = {
    width: '72px',
    marginLeft: 'spacing.8',
};

const houseNumberContainerStyles = (): CSSObject => ({
    '.house-number-container': {
        'div:has(>svg)': {
            visibility: 'hidden',
            '> span': {
                height: 0,
            },
        },
    },
});

const Location = () => {
    const {
        formValues,
        config,
        portal,
        currentStep: { name },
        cities,
    } = useAppContext();
    const {
        control,
        formState: { errors },
        setValue,
    } = useFormContext();
    const { t } = useTranslation(NAME_SPACE);
    const { locale } = useLocale();
    const dispatch = useAppDispatch();

    const [updatedFromParams, setUpdatedFromParams] = useState(false);
    const [houseNumberFocused, setHouseNumberFocused] = useState(false);

    const cityRef = useRef<HTMLInputElement>(null);
    const streetRef = useRef<HTMLInputElement>(null);
    const houseNumberRef = useRef<HTMLInputElement>(null);
    const postalCodeRef = useRef<HTMLInputElement>(null);

    const postalCode = useWatch({ control, name: 'postalCode' });
    const cityName = useWatch({ control, name: 'cityName' });
    const streetName = useWatch({ control, name: 'streetName' });
    const houseNumber = useWatch({ control, name: 'houseNumber' });

    const cityId = useWatch({ control, name: 'cityId' });
    const streetId = useWatch({ control, name: 'streetId' });
    const addressId = useWatch({ control, name: 'addressId' });

    const fields = config[name].getFields(formValues);
    const postalCodeLength = getPostalCodeLength(portal);
    const isGermanPortal = portal === Portal.ImmoWelt || portal === Portal.Immonet;

    useEffect(() => {
        onPostalCodeChanged(postalCode);
    }, [errors.postalCode, postalCode]);

    useEffect(() => {
        onHouseNumberChanged(houseNumber);
    }, [errors.houseNumber, houseNumber]);

    useEffect(() => {
        if (updatedFromParams) {
            focusOnNextField(
                { postalCode, cityName, streetName, houseNumber },
                postalCodeRef,
                houseNumberRef,
            );
            dispatch({
                type: EActionType.SetSpecificTitle,
                payload: 'step_address.header.title_verifyTheAddress',
            });
        }
    }, [updatedFromParams]);

    useEffect(() => {
        /* Prevent from having the addressId to be filled in with last call when the user
        is cleaning the houseNumber field with return button, deleting characters one by one.
        TODO check why debouncePromise is not working properly */
        if (addressId && !houseNumber) {
            updateFormData({ addressId: '' });
        }
    }, [addressId]);

    useEffect(() => {
        const handleFocus = () => !houseNumberFocused && setHouseNumberFocused(true);
        houseNumberRef?.current?.addEventListener('focus', handleFocus);
        return () => houseNumberRef?.current?.removeEventListener('focus', handleFocus);
    }, []);

    useEffect(() => {
        // Get location from handover: auto populate fields with the url parameters
        if ([postalCode, cityName, streetName].some((field) => field === '')) {
            const legacyParams = getLegacyLocationParamsFromURL();
            const params = getLocationParamsFromURL();

            /* First, we are going to try with the parameter geo_id
            Then, with the mobile or legacy parameters (addressId, placeType, id, type)
            Finally, with the location parameters (zip, city_name/location, street, nr) */

            /* TODO https://avivgroup.atlassian.net/wiki/spaces/AGRS/pages/867074333/2024-08-08+Add+Postcode+Layer
            Temporary fix for the postcode layer issue */
            if (params.geoId && !params.geoId.startsWith('POCO')) {
                populateFieldsWithGeoId(params.geoId, params, legacyParams);
            } else populateWithoutGeoId(params, legacyParams);
        }
        return () => {
            dispatch({ type: EActionType.SetSpecificTitle, payload: undefined });
        };
    }, []);

    const dispatchCities = (cities: Array<City>) => {
        dispatch({
            type: EActionType.SetCities,
            payload: cities,
        });
    };

    const updateFormData = (data: Record<string, unknown>) => {
        Object.keys(data).forEach((key) => setValue(key, data[key], { shouldValidate: true }));
    };

    const populateFieldsWithGeoId = async (
        id: string,
        params: PlaceParams,
        legacyParams: LegacyPlaceParams,
    ) => {
        let result = undefined;
        try {
            result = await fetchPlaceApi(id, isGermanPortal, locale);
            updateFormData(result as Record<string, unknown>);
            setUpdatedFromParams(true);
        } catch (e) {
            // if request with geoId fails, try with other params
            populateWithoutGeoId(params, legacyParams);
        }
    };

    const populateFieldsWithAddress = async (paramsData: AutocompleteRequestPayload) => {
        const { text, portal, addressLevel, parentId } = paramsData;
        try {
            const result = await fetchAutocompleteApiForHandover({
                text,
                portal,
                addressLevel,
                parentId,
                isGermanPortal,
                locale,
            });
            if (result.fetchedCities) {
                const fetchedCities = computeCitiesValues(result.fetchedCities);
                dispatchCities(fetchedCities);
            }
            updateFormData(result as unknown as Record<string, unknown>);
            setUpdatedFromParams(true);
        } catch (e) {
            throw new Error('Get location from address parameters failed ');
        }
    };

    const populateFieldsWithLegacyGeoId = async (legacyParams: LegacyPlaceParams) => {
        let result = undefined;
        try {
            result = await fetchMappingApiFromLegacyData(
                portal,
                legacyParams,
                isGermanPortal,
                locale,
            );
            updateFormData(result as Record<string, unknown>);
            setUpdatedFromParams(true);
        } catch (e) {
            throw new Error('Get location from legacy id parameter failed');
        }
    };

    const populateWithoutGeoId = (params: PlaceParams, legacyParams: LegacyPlaceParams) => {
        if (legacyParams.id && legacyParams.type) {
            /* TODO VEGA-1209 remove legacy code
            For the moment, we keep a retro-compatibility. The user can land
            on the funnel with legacy params (id and type) the fields will be autocompleted */
            populateFieldsWithLegacyGeoId({
                id: legacyParams.id,
                type: legacyParams.type,
            });
        } else if (params.postalCode) {
            const { text, addressLevel } = generateHandoverAutocompletePayload({
                ...params,
                isFrenchPortal: isFrenchPortal(portal),
            } as PlaceParamsWithAtLeastPostalCode);
            populateFieldsWithAddress({ text, portal, addressLevel });
        }
    };

    const onPostalCodeChanged = async (value: string) => {
        if (!errors.postalCode && value.length === postalCodeLength) {
            // if the cities are already fetched, we don't need to fetch it again
            let fetchedCities = cities;
            if (!fetchedCities.length || getParamFromURL('zip') !== postalCode) {
                dispatch({
                    type: EActionType.SetToastMessage,
                    payload: undefined,
                });
                try {
                    const result = await fetchAutocompleteAPI({
                        text: postalCode,
                        portal,
                        addressLevel: 'city',
                        limit: 20, // max value
                        isGermanPortal,
                        locale,
                    });
                    fetchedCities = computeCitiesValues(result);
                } catch (e) {
                    dispatch({
                        type: EActionType.SetToastMessage,
                        payload: { status: LocationError.CityNameException },
                    });
                    throw new Error('Get autocompleted cities list failed');
                }
            }

            if (fetchedCities.length === 0) {
                dispatch({
                    type: EActionType.SetToastMessage,
                    payload: { status: LocationError.CityNameNotFound },
                });
            }

            if (fetchedCities.length === 1) {
                const { id, value } = fetchedCities[0];
                updateFormData({
                    cityId: id,
                    cityName: value,
                });
            }

            if (fetchedCities.length > 1) {
                const city = fetchedCities.find((city: City) => city.value === cityName);
                if (city?.id !== cityId) {
                    updateFormData({
                        cityId: city?.id ?? '',
                    });
                }
            }
            dispatchCities(fetchedCities);
        } else {
            updateFormData({
                cityName: '',
                cityId: '',
                streetName: '',
                streetId: '',
                houseNumber: '',
                addressId: '',
            });
            dispatchCities([]);
        }
    };

    const fetchAddresses = async (value: string) => {
        return await fetchGeocodingAPI(
            {
                country: getPortalCountry(portal),
                number: value,
                address: streetName,
                city: cityName,
                postal_code: postalCode,
            },
            streetId,
        );
    };

    const onHouseNumberChanged = async (inputValue: string) => {
        try {
            if (inputValue) {
                /* Check if houseNumber field has been focused to prevent useless call to fetchAddresses (geocoding api) :
                when user entered on the funnel with params, the autocomplete api has been called and potentially used to
                fill in the house number field, that unnecessarly triggers onHouseNumberChanged */
                if (houseNumberFocused) {
                    const address = await debouncePromise(
                        async () => fetchAddresses(inputValue),
                        300,
                    );
                    const newAddressId = address?.match.id ?? '';
                    if (newAddressId !== addressId) {
                        updateFormData({ addressId: newAddressId });
                    }
                }
            } else {
                // When user selects all the digits and then delete
                updateFormData({ addressId: '' });
            }
        } catch (e) {
            addError(e, { payload: inputValue });
        }
    };

    const overridenPostalCodeField: Field = {
        ...fields.postalCode,
        triggerSideEffect: () => {
            // reset fields
            updateFormData({
                cityName: '',
                cityId: '',
                streetName: '',
                streetId: '',
                houseNumber: '',
                addressId: '',
            });
            dispatchCities([]);
        },
    };

    const overridenCityNameField: Field = {
        ...fields.cityName,
        options: {
            ...fields.cityName.options,
            values: cities,
        },
        triggerSideEffect: (value: unknown) => {
            const selectedCity = cities?.find((city: any) => city.value === value);
            updateFormData({
                streetName: '',
                streetId: '',
                houseNumber: '',
                addressId: '',
                cityId: selectedCity?.id,
            });
        },
    };

    return (
        <>
            <FieldWrapper name="postalCode" field={overridenPostalCodeField} ref={postalCodeRef} />
            <FieldWrapper
                name="cityName"
                field={overridenCityNameField}
                hidePlaceholder={cities?.length === 0}
                ref={cityRef}
            />
            <FieldsetWrapper
                name="address"
                legendTitle={t('step_address.fieldsets.address.legend')}
                styles={addressContainerStyles}
            >
                <FieldWrapper
                    name="streetName"
                    styles={streetNameStyles}
                    field={fields.streetName}
                    ref={streetRef}
                />
                <Global styles={houseNumberContainerStyles} />
                <Box unsafe_className="house-number-container">
                    <FieldWrapper
                        name="houseNumber"
                        field={fields.houseNumber}
                        styles={houseNumberStyles}
                        ref={houseNumberRef}
                    />
                </Box>
            </FieldsetWrapper>
        </>
    );
};

export default Location;
